import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchAttachmentBlob } from '@/api/media';
import type { MessageAttachment } from '@/types';
import { ImageLightbox } from './ImageLightbox';

/**
 * A small step picture shown beside the line that carries its marker in a bot reply under review. Click shows the
 * picture larger in the same window (ImageLightbox), never in a new tab. A picture that cannot be fetched becomes a quiet text chip, the step text untouched.
 */
export const InlineStepPicture: React.FC<{ attachment: MessageAttachment }> = ({ attachment }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    setFailed(false);
    setObjectUrl(null);
    fetchAttachmentBlob(attachment.fileId, attachment.plane)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setObjectUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [attachment.fileId, attachment.plane]);

  if (failed) {
    return (
      <span data-testid="inline-step-picture-unavailable" title={attachment.fileName} style={{ margin: '0 6px', fontSize: 11, opacity: 0.7 }}>
        {'\u{1F5BC}'}
      </span>
    );
  }
  if (!objectUrl) {
    return <span data-testid="inline-step-picture-loading" aria-hidden="true" style={{ display: 'inline-block', width: 56, height: 56, margin: '0 6px', borderRadius: 6, background: 'rgba(0,0,0,0.06)', verticalAlign: 'middle' }} />;
  }
  return (
    <>
      <button
        type="button"
        data-testid="inline-step-picture"
        aria-label={`Open full-size image: ${attachment.fileName}`}
        onClick={() => setLightbox(true)}
        style={{ display: 'inline-block', width: 56, height: 56, margin: '0 6px', padding: 0, border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, overflow: 'hidden', background: 'none', lineHeight: 0, cursor: 'zoom-in', verticalAlign: 'middle' }}
      >
        <img src={objectUrl} alt={attachment.fileName} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </button>
      {lightbox && <ImageLightbox url={objectUrl} fileName={attachment.fileName} onClose={() => setLightbox(false)} />}
    </>
  );
};

/**
 * Renders already-sanitized html and mounts an InlineStepPicture into each `<span data-media-k>` through a portal, so
 * the list structure and numbering from the markdown survive (no string-splitting of the html).
 */
export const InlineMediaText: React.FC<{
  html: string;
  attachments: MessageAttachment[];
  testId?: string;
  className?: string;
}> = ({ html, attachments, testId, className }) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [mounts, setMounts] = useState<Array<{ k: number; el: Element }>>([]);

  useLayoutEffect(() => {
    const nodes = rootRef.current?.querySelectorAll('span[data-media-k]');
    const found: Array<{ k: number; el: Element }> = [];
    nodes?.forEach((el) => {
      const k = Number.parseInt(el.getAttribute('data-media-k') ?? '', 10);
      if (Number.isInteger(k) && k >= 1 && attachments[k - 1]) found.push({ k, el });
    });
    setMounts(found);
  }, [html, attachments]);

  return (
    <>
      <div ref={rootRef} className={className} data-testid={testId} dir="auto" dangerouslySetInnerHTML={{ __html: html }} />
      {mounts.map(({ k, el }) => createPortal(<InlineStepPicture attachment={attachments[k - 1]} />, el, `inline-${k}`))}
    </>
  );
};
